import { type CreateCruiseCabinRequest, type CruiseCabin } from '@utazzo/shared';
import {
  UzButton,
  UzCombobox,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { RoomTypePicker } from '@/components/pickers';

/** Common cruise deck presets. Free-text still allowed via the combobox's
 *  "create" row so a specific deck (e.g. "Deck 11") can be typed. */
const DECK_PRESETS = ['Lower Deck', 'Mid Deck', 'Upper Deck'] as const;

/**
 * Create/edit dialog for one cruise cabin.
 *
 * The cabin owns its catalog identity only - name, category, deck,
 * occupancy. All pricing (per-pax rates, extra beds, meal supplements) lives
 * on the cabin's rate cards (validity windows), edited on the Cabins & rates
 * tab, so it can vary by sailing/season.
 */

interface CabinFormValues {
  name: string;
  roomTypeId: string | null;
  deck: string | null;
  description: string | null;
  minOccupancy: number;
  maxOccupancy: number;
  cabinInventoryCount: number;
  isActive: boolean;
}

const CABIN_DEFAULTS: CabinFormValues = {
  name: '',
  roomTypeId: null,
  deck: null,
  description: null,
  minOccupancy: 1,
  maxOccupancy: 2,
  cabinInventoryCount: 0,
  isActive: true,
};

interface CabinFormDialogProps {
  mode: 'create' | 'edit';
  cabin?: CruiseCabin;
  onClose: () => void;
  onSubmit: (values: CreateCruiseCabinRequest) => Promise<void>;
  pending: boolean;
}

export const CabinFormDialog = ({
  mode,
  cabin,
  onClose,
  onSubmit,
  pending,
}: CabinFormDialogProps): React.JSX.Element => {
  const form = useForm<CabinFormValues>({ defaultValues: CABIN_DEFAULTS });

  useEffect(() => {
    if (mode === 'create') {
      form.reset(CABIN_DEFAULTS);
      return;
    }
    if (cabin) {
      form.reset({
        name: cabin.name,
        roomTypeId: cabin.roomTypeId,
        deck: cabin.deck,
        description: cabin.description,
        minOccupancy: cabin.minOccupancy,
        maxOccupancy: cabin.maxOccupancy,
        cabinInventoryCount: cabin.cabinInventoryCount,
        isActive: cabin.isActive,
      });
    }
  }, [mode, cabin, form]);

  const submit = form.handleSubmit(async (values) => {
    if (values.maxOccupancy < values.minOccupancy) {
      form.setError('maxOccupancy', {
        type: 'min',
        message: 'Max occupancy must be >= min occupancy',
      });
      return;
    }
    await onSubmit({
      name: values.name,
      roomTypeId: values.roomTypeId,
      deck: values.deck,
      description: values.description,
      minOccupancy: values.minOccupancy,
      maxOccupancy: values.maxOccupancy,
      cabinInventoryCount: values.cabinInventoryCount,
      isActive: values.isActive,
    });
  });

  return (
    <UzDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={mode === 'create' ? 'Add cabin category' : `Edit ${cabin?.name ?? 'cabin'}`}
      description="Identity, deck and occupancy. Prices, extra beds and meals are added per sailing window on the cabin afterwards."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={onClose} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={submit} loading={pending}>
            {mode === 'create' ? 'Add cabin' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      <UzForm form={form} onSubmit={submit}>
        <div className="space-y-5">
          <UzFormField name="name">
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | undefined) ?? ''}
                label="Cabin name"
                placeholder="e.g. Deluxe Balcony, Ocean View Suite"
                error={error}
                required
                disabled={pending}
              />
            )}
          </UzFormField>

          <UzFormField name="roomTypeId">
            {({ field, error }) => (
              <RoomTypePicker
                label="Cabin category"
                value={(field.value as string | null | undefined) ?? null}
                onValueChange={(v) => field.onChange(v)}
                disabled={pending}
                error={error}
              />
            )}
          </UzFormField>

          <UzFormField name="deck">
            {({ field, error }) => {
              const current = (field.value as string | null | undefined) ?? '';
              // Presets + the current value (so an existing custom deck like
              // "Deck 11" stays selectable), de-duplicated.
              const options = Array.from(
                new Set([...DECK_PRESETS, ...(current ? [current] : [])]),
              ).map((d) => ({ value: d, label: d }));
              return (
                <UzCombobox
                  name={field.name}
                  label="Deck"
                  hint="Pick a deck level, or type a specific deck (e.g. Deck 11) and press the create row."
                  placeholder="Select or type a deck"
                  searchPlaceholder="Search or type a deck..."
                  options={options}
                  value={current || undefined}
                  onValueChange={(v) => field.onChange(v || null)}
                  onCreate={(label) => field.onChange(label.trim() || null)}
                  onClear={() => field.onChange(null)}
                  clearable
                  error={error}
                  disabled={pending}
                />
              );
            }}
          </UzFormField>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <UzFormField name="minOccupancy">
              {({ field, error }) => (
                <UzNumberInput
                  name={field.name}
                  label="Min occupancy"
                  precision={0}
                  min={1}
                  value={
                    field.value === null || field.value === undefined ? 1 : (field.value as number)
                  }
                  onValueChange={(v) => field.onChange(v ?? 1)}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="maxOccupancy">
              {({ field, error }) => (
                <UzNumberInput
                  name={field.name}
                  label="Max occupancy"
                  precision={0}
                  min={1}
                  value={
                    field.value === null || field.value === undefined ? 2 : (field.value as number)
                  }
                  onValueChange={(v) => field.onChange(v ?? 2)}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
          </div>

          <UzFormField name="cabinInventoryCount">
            {({ field, error }) => (
              <UzNumberInput
                name={field.name}
                label="Cabins available"
                precision={0}
                min={0}
                value={
                  field.value === null || field.value === undefined ? 0 : (field.value as number)
                }
                onValueChange={(v) => field.onChange(v ?? 0)}
                hint="How many physical cabins of this category the ship has. 0 = no cap."
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>

          <UzFormField name="description">
            {({ field, error }) => (
              <UzTextarea
                {...field}
                value={(field.value as string | null | undefined) ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                label="Description"
                placeholder="View, amenities, what's included..."
                rows={3}
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>

          <UzFormField name="isActive">
            {({ field }) => (
              <UzSwitch
                label="Active"
                description="Inactive cabins are hidden from the itinerary picker."
                checked={field.value !== false}
                onCheckedChange={field.onChange}
                disabled={pending}
              />
            )}
          </UzFormField>
        </div>
      </UzForm>
    </UzDrawer>
  );
};
